import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, CheckCircle, X, ChevronLeft, ChevronRight } from "lucide-react";
import sticker from "@/assets/sticker-watermark.png";

const KitchenCabinets = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      title: "خزائن المطبخ الفاخرة",
      subtitle: "جودة ألمانية وأناقة إيطالية",
      description: "اصنع مطبخ أحلامك مع خزائن الفنون! نستخدم خامات عالمية فاخرة تضمن لك مطبخاً يدوم لسنوات طويلة. خشب شيبورد ألماني بسماكة 18 ملم مقاوم للرطوبة والحرارة، مع إكسسوارات إيطالية فاخرة تشمل مفصلات ناعمة الإغلاق وسحابات أدراج متينة ومسكات عصرية أنيقة.",
      features: [
        "شيبورد ألماني 18 ملم",
        "مفصلات إيطالية ناعمة الإغلاق",
        "سحابات أدراج إيطالية متينة",
        "مسكات مودرن أنيقة",
        "مقاوم للرطوبة والحرارة",
        "ضمان شامل 5 سنوات"
      ],
      whyChoose: "لماذا خزائن الفنون؟",
      benefits: [
        { title: "خامات ألمانية أصلية", description: "شيبورد ألماني بسماكة 18 ملم عالي الكثافة، مقاوم للرطوبة والحرارة، معالج ضد التلف لضمان عمر أطول لخزائنكم." },
        { title: "إكسسوارات إيطالية فاخرة", description: "مفصلات ناعمة الإغلاق وسحابات أدراج إيطالية متينة تضمن أداءً سلساً وعمراً افتراضياً طويلاً حتى مع الاستخدام اليومي المكثف." },
        { title: "تصميم حسب الطلب", description: "فريق تصميم متخصص يصمم مطبخك وفقاً لمساحتك واحتياجاتك الخاصة مع مسكات مودرن تناسب ذوقك." },
        { title: "تركيب وضمان شامل", description: "تركيب احترافي من فريق متخصص مع ضمان شامل 5 سنوات وخدمة ما بعد البيع المتميزة." }
      ],
      types: "أنواع خزائن المطبخ",
      typesList: [
        { title: "خزائن علوية", description: "خزائن علوية بتصاميم متنوعة مع مفصلات إيطالية ناعمة الإغلاق توفر مساحة تخزين إضافية وتضفي أناقة على مطبخكم." },
        { title: "خزائن سفلية", description: "خزائن سفلية متينة من الشيبورد الألماني مع أدراج بسحابات إيطالية فائقة النعومة لتخزين أدوات المطبخ." },
        { title: "خزائن زاوية", description: "حلول ذكية للاستفادة من مساحات الزوايا مع آليات دوارة إيطالية سهلة الاستخدام." },
        { title: "جزر المطبخ", description: "جزر مطبخ أنيقة مع مساحات تخزين واسعة ومسكات مودرن تضيف لمسة عصرية لمطبخك." }
      ],
      materialsTitle: "المواصفات الفنية",
      materials: [
        { title: "الخشب", spec: "شيبورد ألماني عالي الجودة بسماكة 18 ملم" },
        { title: "المفصلات", spec: "مفصلات إيطالية ناعمة الإغلاق (Soft Close)" },
        { title: "سحابات الأدراج", spec: "سحابات إيطالية متينة بنظام الإغلاق الناعم" },
        { title: "المسكات", spec: "مسكات مودرن بتصاميم عصرية متنوعة" }
      ],
      contactTitle: "احصل على استشارة مجانية",
      contactText: "تواصل معنا الآن للحصول على تصميم مخصص لمطبخك مع أفضل الخامات الألمانية والإكسسوارات الإيطالية.",
      contactButton: "تواصل معنا",
      backToWood: "العودة لمنتجات الخشب"
    },
    en: {
      title: "Premium Kitchen Cabinets",
      subtitle: "German Quality & Italian Elegance",
      description: "Create your dream kitchen with Alfunoon! We use world-class premium materials that guarantee a kitchen that lasts for years. German chipboard 18mm thick, moisture and heat resistant, with premium Italian accessories including soft-close hinges, durable drawer slides, and elegant modern handles.",
      features: [
        "German Chipboard 18mm",
        "Italian Soft-Close Hinges",
        "Italian Durable Drawer Slides",
        "Elegant Modern Handles",
        "Moisture & Heat Resistant",
        "5-Year Comprehensive Warranty"
      ],
      whyChoose: "Why Alfunoon Cabinets?",
      benefits: [
        { title: "Genuine German Materials", description: "High-density German chipboard 18mm thick, moisture and heat resistant, treated against damage to ensure longer life for your cabinets." },
        { title: "Premium Italian Accessories", description: "Soft-close hinges and durable Italian drawer slides ensure smooth performance and long lifespan even with intensive daily use." },
        { title: "Custom Design", description: "Specialized design team creates your kitchen according to your space and specific needs with modern handles to match your taste." },
        { title: "Installation & Warranty", description: "Professional installation by specialized team with comprehensive 5-year warranty and excellent after-sales service." }
      ],
      types: "Types of Kitchen Cabinets",
      typesList: [
        { title: "Upper Cabinets", description: "Upper cabinets with various designs featuring Italian soft-close hinges providing additional storage space and adding elegance to your kitchen." },
        { title: "Lower Cabinets", description: "Sturdy lower cabinets made from German chipboard with Italian ultra-smooth drawer slides for storing kitchen utensils." },
        { title: "Corner Cabinets", description: "Smart solutions to utilize corner spaces with Italian easy-to-use rotating mechanisms." },
        { title: "Kitchen Islands", description: "Elegant kitchen islands with spacious storage and modern handles adding a contemporary touch to your kitchen." }
      ],
      materialsTitle: "Technical Specifications",
      materials: [
        { title: "Wood", spec: "High-quality German chipboard 18mm thick" },
        { title: "Hinges", spec: "Italian soft-close hinges" },
        { title: "Drawer Slides", spec: "Durable Italian slides with soft-close system" },
        { title: "Handles", spec: "Modern handles with various contemporary designs" }
      ],
      contactTitle: "Get a Free Consultation",
      contactText: "Contact us now for a custom design for your kitchen with the best German materials and Italian accessories.",
      contactButton: "Contact Us",
      backToWood: "Back to Wood Products"
    }
  };

  const t = content[language];

  const galleryImages = [
    "/lovable-uploads/kitchen-1.jpg",
    "/lovable-uploads/kitchen-2.jpg",
    "/lovable-uploads/kitchen-3.jpg",
    "/lovable-uploads/kitchen-4.jpg",
    "/lovable-uploads/kitchen-5.jpg",
    "/lovable-uploads/kitchen-6.jpg",
    "/lovable-uploads/kitchen-7.jpg",
    "/lovable-uploads/kitchen-8.jpg",
    "/lovable-uploads/kitchen-9.jpg",
    "/lovable-uploads/kitchen-10.jpg",
    "/lovable-uploads/kitchen-11.jpg",
    "/lovable-uploads/kitchen-12.jpg",
    "/lovable-uploads/kitchen-13.jpg",
    "/lovable-uploads/kitchen-14.jpg",
    "/lovable-uploads/kitchen-15.jpg",
    "/lovable-uploads/kitchen-16.jpg",
    "/lovable-uploads/kitchen-17.jpg",
    "/lovable-uploads/kitchen-18.jpg",
    "/lovable-uploads/kitchen-19.jpg",
    "/lovable-uploads/kitchen-20.jpg"
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-wood/20 to-wood/5 py-16">
        <div className="container mx-auto px-4">
          <Link 
            to="/wood" 
            className="inline-flex items-center gap-2 text-wood hover:text-wood/80 mb-6 transition-colors"
          >
            {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
            {t.backToWood}
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-kufi font-bold text-wood mb-4">{t.title}</h1>
            <p className="text-xl text-wood/80 mb-6">{t.subtitle}</p>
            <p className="text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {t.description}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-md p-6 mb-12"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {t.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-gray-700">
                <CheckCircle className="w-5 h-5 text-wood flex-shrink-0" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Why Choose Us & Technical Specifications with Background */}
        <div className="relative mb-12 rounded-lg overflow-hidden">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url('/lovable-uploads/kitchen-1.jpg')`,
              opacity: 0.20
            }}
          />
          
          <div className="relative z-10 py-8 px-4">
            {/* Why Choose Us */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-kufi font-bold text-center mb-8 text-wood">{t.whyChoose}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {t.benefits.map((benefit, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow bg-white/95">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-lg mb-2 text-wood font-kufi">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>

            {/* Technical Specifications */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-kufi font-bold text-center mb-8 text-wood">{t.materialsTitle}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {t.materials.map((material, index) => (
                  <div key={index} className="bg-white/95 p-6 rounded-lg shadow-md text-center">
                    <h3 className="font-bold text-lg mb-2 text-wood font-kufi">{material.title}</h3>
                    <p className="text-gray-600">{material.spec}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Types of Cabinets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-wood/5 rounded-lg p-8 mb-12"
        >
          <h2 className="text-2xl font-kufi font-bold text-center mb-8 text-wood">{t.types}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.typesList.map((type, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-bold text-lg mb-2 text-wood font-kufi">{type.title}</h3>
                <p className="text-gray-600">{type.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index} 
                className="relative rounded-lg overflow-hidden shadow-md cursor-pointer group"
                onClick={() => {
                  setCurrentImageIndex(index);
                  setLightboxOpen(true);
                }}
              >
                <img 
                  src={image} 
                  alt={`${t.title} ${index + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {/* Watermark */}
                <div className="absolute bottom-4 left-4 opacity-70 pointer-events-none">
                  <img 
                    src={sticker} 
                    alt="ALFUNOON" 
                    className="w-24 h-auto drop-shadow-lg"
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Lightbox */}
        <AnimatePresence>
          {lightboxOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
              onClick={() => setLightboxOpen(false)}
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
                onClick={() => setLightboxOpen(false)}
              >
                <X className="w-8 h-8" />
              </button>

              {/* Previous Button */}
              <button
                className="absolute left-4 text-white hover:text-gray-300 transition-colors z-50 p-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
                }}
              >
                <ChevronLeft className="w-10 h-10" />
              </button>

              {/* Image */}
              <motion.img
                key={currentImageIndex}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                src={galleryImages[currentImageIndex]}
                alt={`${t.title} ${currentImageIndex + 1}`}
                className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />

              {/* Next Button */}
              <button
                className="absolute right-4 text-white hover:text-gray-300 transition-colors z-50 p-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
                }}
              >
                <ChevronRight className="w-10 h-10" />
              </button>

              {/* Image Counter */}
              <div className="absolute bottom-4 text-white text-sm">
                {currentImageIndex + 1} / {galleryImages.length}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center bg-wood/10 p-8 rounded-lg"
        >
          <h2 className="text-2xl font-kufi font-bold mb-4 text-wood">{t.contactTitle}</h2>
          <p className="max-w-2xl mx-auto mb-6 text-gray-700">
            {t.contactText}
          </p>
          <Link to="/contact">
            <Button className="bg-wood hover:bg-wood/90 text-white font-kufi py-2 px-6 rounded-md gap-2">
              <Phone className="w-4 h-4" />
              {t.contactButton}
            </Button>
          </Link>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default KitchenCabinets;
